import type { inferRouterOutputs } from "@trpc/server";
import { Link } from "react-router";
import { useIntl } from "react-intl";

import type { AppRouter } from "@runcheetah/api/router";
import { Button } from "@runcheetah/ui";

import { buildBrandPath } from "@/hooks/use-route-brand";
import { trpc } from "@/lib/trpc";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type BillingOverview = RouterOutputs["brand"]["getBillingOverview"];

const formatDate = (value: string | null, locale: string): string | null => {
  if (!value) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(value));
};

export function FailedPaymentBanner({
  brandId,
  overview,
  compact = false,
}: {
  brandId: string;
  overview: BillingOverview | null | undefined;
  compact?: boolean;
}) {
  const intl = useIntl();
  const account = overview?.account;
  if (account?.status !== "past_due") {
    return null;
  }

  const graceEndsAt = formatDate(account.failedPaymentGraceEndsAt, intl.locale);
  const toneClassName = account.isFailedPaymentGraceExpired
    ? "border-red-800/20 bg-red-50 text-red-950"
    : "border-amber-300/70 bg-amber-50 text-amber-950";
  const title = account.isFailedPaymentGraceExpired
    ? intl.formatMessage({
        id: "billing.failedPayment.expiredTitle",
        defaultMessage: "A quick billing update",
      })
    : intl.formatMessage({
        id: "billing.failedPayment.activeTitle",
        defaultMessage: "A quick billing update",
      });
  const message = account.isFailedPaymentGraceExpired
    ? intl.formatMessage({
        id: "billing.failedPayment.expiredMessage",
        defaultMessage:
          "We couldn't collect your latest payment. Please update your payment method to resume generating content and continue using Cheetah.",
      })
    : graceEndsAt
      ? intl.formatMessage(
          {
            id: "billing.failedPayment.activeMessageWithDate",
            defaultMessage:
              "We couldn't collect your latest payment, but everything stays available until {date}. Please update your payment method when you have a moment.",
          },
          { date: graceEndsAt },
        )
      : intl.formatMessage({
          id: "billing.failedPayment.activeMessage",
          defaultMessage:
            "We couldn't collect your latest payment. Please update your payment method when you have a moment.",
        });

  return (
    <div className={`border-b px-4 py-3 ${toneClassName}`} role="status">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm font-semibold">{title}</p>
          {!compact ? <p className="text-sm leading-5 opacity-90">{message}</p> : null}
        </div>
        <Button
          asChild
          size="sm"
          variant={account.isFailedPaymentGraceExpired ? "destructive" : "outline"}
        >
          <Link to={buildBrandPath(brandId, "/settings/billing")}>
            {intl.formatMessage({
              id: "billing.failedPayment.resolveCta",
              defaultMessage: "Update payment method",
            })}
          </Link>
        </Button>
      </div>
    </div>
  );
}

export function GlobalFailedPaymentBanner({ brandId }: { brandId: string | undefined }) {
  const billingAvailabilityQuery = trpc.brand.getBillingAvailability.useQuery(
    { brandId: brandId ?? "" },
    { enabled: Boolean(brandId), staleTime: 60_000 },
  );
  const billingOverviewQuery = trpc.brand.getBillingOverview.useQuery(
    { brandId: brandId ?? "" },
    {
      enabled: Boolean(brandId && billingAvailabilityQuery.data?.enabled),
      refetchOnWindowFocus: true,
      staleTime: 30_000,
    },
  );

  if (!brandId) {
    return null;
  }

  return <FailedPaymentBanner brandId={brandId} overview={billingOverviewQuery.data} />;
}
